import { MAX_LOD_SWITCH_DISTANCE } from "@repo/api/src/schemas/lod";
import { MAX_LOD_REDUCED_LEVELS } from "../../utils/lod-generation-options";
import { Alert, Button, Input } from "../ui";

export function LodLevelSettings({
	ja,
	percentages,
	distances,
	levelError,
	distanceError,
	editablePoints,
	onPercentagesChange,
	onDistanceChange,
	onResetDistances,
}: {
	ja: boolean;
	percentages: string[];
	distances: string[] | null;
	levelError: string | null;
	distanceError: string | null;
	editablePoints: boolean;
	onPercentagesChange: (values: string[]) => void;
	onDistanceChange: (index: number, value: string) => void;
	onResetDistances: () => void;
}) {
	return (
		<section
			className="account-lod-level-settings account-dialog-section"
			aria-label={ja ? "LODの設定" : "LOD settings"}
		>
			<div className="account-preview-heading">
				<h3>{ja ? "段階と切り替え距離" : "Levels and switch distances"}</h3>
				<Button variant="ghost" size="sm" onClick={onResetDistances} disabled={distances === null}>
					{ja ? "距離を初期値に戻す" : "Reset distances"}
				</Button>
			</div>
			<table className="account-lod-level-table">
				<thead>
					<tr>
						<th scope="col">{ja ? "段階" : "Level"}</th>
						<th scope="col">{ja ? "点数（%）" : "Splats (%)"}</th>
						<th scope="col">{ja ? "切り替え距離" : "Switch distance"}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<th scope="row">LOD 0</th>
						<td>100%</td>
						<td className="account-dialog-caption">{ja ? "近く" : "Nearby"}</td>
					</tr>
					{percentages.map((value, index) => (
						<tr key={`lod-${index + 1}`}>
							<th scope="row">LOD {index + 1}</th>
							<td>
								<div className="account-lod-percent-field">
									<Input
										aria-label={ja ? `LOD ${index + 1}の点数（%）` : `LOD ${index + 1} splats (%)`}
										type="number"
										min="0"
										max="100"
										step="any"
										value={value}
										disabled={!editablePoints}
										aria-invalid={!!levelError}
										aria-describedby={levelError ? "lod-level-validation" : undefined}
										onChange={event =>
											onPercentagesChange(
												percentages.map((current, position) => (position === index ? event.target.value : current)),
											)
										}
									/>
									<span aria-hidden="true">%</span>
								</div>
							</td>
							<td>
								<Input
									aria-label={ja ? `LOD ${index + 1}に切り替える距離` : `LOD ${index + 1} switch distance`}
									type="number"
									min="0"
									max={MAX_LOD_SWITCH_DISTANCE}
									step="any"
									value={distances?.[index] ?? ""}
									placeholder={ja ? "自動" : "Auto"}
									aria-invalid={!!distanceError}
									aria-describedby={distanceError ? "lod-distance-validation" : "lod-distance-help"}
									onChange={event => onDistanceChange(index, event.target.value)}
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<p id="lod-distance-help" className="account-dialog-caption">
				{ja
					? "距離は視点から各部分までの値です。単位はモデルの座標に従います。未設定の場合は自動で切り替えます。"
					: "Distances are measured from the camera to each part, in model coordinate units. Unset distances switch automatically."}
			</p>
			{levelError && (
				<div id="lod-level-validation">
					<Alert tone="error">{levelError}</Alert>
				</div>
			)}
			{distanceError && (
				<div id="lod-distance-validation">
					<Alert tone="error">{distanceError}</Alert>
				</div>
			)}
			{editablePoints && (
				<>
					<p className="account-dialog-caption">
						{ja
							? "点数は元モデルに対する割合です。点数の変更にはLODの作り直しが必要です。"
							: "Splat counts are relative to the original. Changing counts requires regenerating LOD data."}
					</p>
					<div className="account-lod-level-actions">
						<Button
							variant="secondary"
							size="sm"
							disabled={percentages.length >= MAX_LOD_REDUCED_LEVELS || !!levelError || !!distanceError}
							onClick={() => onPercentagesChange([...percentages, String(Number(percentages.at(-1)) / 2)])}
						>
							{ja ? "段階を追加" : "Add level"}
						</Button>
						<Button
							variant="ghost"
							size="sm"
							disabled={percentages.length <= 1}
							onClick={() => onPercentagesChange(percentages.slice(0, -1))}
						>
							{ja ? "最後の段階を削除" : "Remove last level"}
						</Button>
					</div>
				</>
			)}
		</section>
	);
}
